import { format, formatDistanceToNow } from 'date-fns';
import { Link } from 'react-router';
import { Badge } from '@/components/ui/badge';
import ConfigText from '@/components/ConfigText';
import { cn } from '@/lib/utils';
import { History } from 'lucide-react';
import {
  type ConfigRestore,
  type ConfigRestoreStatus,
  type ConfigSnapshotListItem,
  useConfigRestores,
} from '@/hooks/useConfigSnapshots';

const STATUS_STYLE: Record<ConfigRestoreStatus, string> = {
  queued: 'bg-muted text-muted-foreground',
  running: 'bg-info/15 text-info',
  succeeded: 'bg-success/15 text-success',
  restored_with_differences: 'bg-sev-3/20 text-foreground',
  failed: 'bg-destructive/15 text-destructive',
  unknown: 'bg-warning/15 text-warning',
};

const STATUS_LABEL: Record<ConfigRestoreStatus, string> = {
  queued: 'queued',
  running: 'restoring',
  succeeded: 'restored',
  restored_with_differences: 'restored with differences',
  failed: 'failed',
  unknown: 'no reply',
};

/** How a restore went, as a badge. */
export function RestoreStatusBadge({ status, className }: { status: ConfigRestoreStatus; className?: string }) {
  return (
    <Badge className={cn('border-0 font-normal', STATUS_STYLE[status], className)}>
      {STATUS_LABEL[status]}
    </Badge>
  );
}

/** When a restore was asked for, and when it finished. */
export function RestoreTimes({ restore }: { restore: Pick<ConfigRestore, 'createdAt' | 'finishedAt'> }) {
  return (
    <span className="text-muted-foreground">
      asked {formatDistanceToNow(new Date(restore.createdAt), { addSuffix: true })}
      {restore.finishedAt
        ? `, finished ${formatDistanceToNow(new Date(restore.finishedAt), { addSuffix: true })}`
        : null}
    </span>
  );
}

/**
 * Recent restores of a device and how each went. Hidden until the device has
 * had one.
 */
export default function ConfigRestoreHistory({ deviceId }: { deviceId: string }) {
  const { data: restores } = useConfigRestores(deviceId);
  if (!restores || restores.length === 0) {
    return null;
  }

  return (
    <section className="bg-card border border-border rounded-lg p-5">
      <h2 className="text-sm font-semibold mb-3">Restores</h2>
      <ol className="space-y-3">
        {restores.map((r) => (
          <li key={r.id} className="text-xs">
            <div className="flex flex-wrap items-center gap-2">
              <RestoreStatusBadge status={r.status} />
              <RestoreTimes restore={r} />
              {r.changeManagementId ? (
                <Link to={`/change-management/${r.changeManagementId}`} className="text-primary hover:underline">
                  rolling back a change
                </Link>
              ) : null}
            </div>
            {r.detail ? (
              <ConfigText
                text={r.detail}
                className="mt-1 whitespace-pre-wrap font-mono text-[11px] text-muted-foreground"
              />
            ) : null}
          </li>
        ))}
      </ol>
    </section>
  );
}

/**
 * A restore as a line of the config timeline: when it ran, the snapshot it put
 * back, and how it went. Its detail is in the Restores list above.
 */
export function RestoreTimelineItem({
  restore,
  target,
}: {
  restore: ConfigRestore;
  target: ConfigSnapshotListItem | null;
}) {
  return (
    <li className="relative">
      <span className="absolute -left-6 top-0 inline-flex items-center justify-center w-6 h-6 rounded-full bg-bg-subtle border border-border text-muted-foreground">
        <History className="w-3 h-3" aria-hidden="true" />
      </span>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="text-muted-foreground">
          {formatDistanceToNow(new Date(restore.startedAt ?? restore.createdAt), { addSuffix: true })}
        </span>
        <span>
          {target
            ? `Restore to the snapshot of ${format(new Date(target.createdAt), 'PP p')}`
            : 'Restore to a snapshot no longer kept'}
        </span>
        <RestoreStatusBadge status={restore.status} className="text-[10px]" />
      </div>
    </li>
  );
}

